// React-Flow island — hooks permitted per CLAUDE.md island exception
// (extended from the lineage canvas to the RI read-only renderer). Read-only.
//
// Behaviors ported from the lineage canvas: click a node to highlight its full
// upstream + downstream dependency path (dimming the rest), immediate-vs-full
// highlight depth, upstream/downstream edge colour-coding, animated edges for
// directional flow, hover preview, and a minimap. The flip-card node (RiColumnNode)
// reveals columns when the global "Column detail" toggle is on.
import React from 'react';
import {ReactFlow, Controls, Background, MiniMap, MarkerType} from '@xyflow/react';
import dagre from 'dagre';
import '@xyflow/react/dist/style.css';
import RiColumnNode from './RiColumnNode';
import {filterGraph} from './riFilters';

const nodeTypes = {riColumn: RiColumnNode};

const NODE_WIDTH = 230;
const FRONT_H = 52;
const BACK_HEADER = 40;
const ROW_H = 26;
const MORE_H = 24;
const MAX_EXTRA_COLS = 5;

const UP_COLOR = '#2E7D32'; // upstream — what the object depends on
const DOWN_COLOR = '#EF6C00'; // downstream — what depends on the object
const FK_COLOR = '#1565C0';
const VIEW_COLOR = '#026684';
const CIRCULAR_COLOR = '#F44336';
const DIM_EDGE = '#d0d0d0';

function baseEdge(relType) {
  if (relType === 'CIRCULAR') return {color: CIRCULAR_COLOR, dash: '4 3'};
  if (relType === 'VIEW_DEP') return {color: VIEW_COLOR, dash: '6 3'};
  return {color: FK_COLOR, dash: null};
}

// One node "spec" — the display columns + height, shared by layout and rendering.
// `connectedColIds` is the set of column ids that participate in an FK edge (from the
// edges themselves) — the authoritative source of which columns need handles, so
// column-level edges connect even when is_primary_key/is_foreign_key aren't populated.
function buildNodeSpec(n, columnsByObject, columnDetail, connectedColIds) {
  const raw = (columnDetail && columnsByObject && columnsByObject[n.objectId]) || n.columns || [];
  const cols = raw.map((c) => ({...c, connected: connectedColIds.has(c.columnId)}));
  const isKey = (c) => c.isFk || c.isPk || c.connected;
  const keyCols = cols.filter(isKey);
  const otherCols = cols.filter((c) => !isKey(c));
  const displayCols = columnDetail ? [...keyCols, ...otherCols.slice(0, MAX_EXTRA_COLS)] : [];
  const moreCount = columnDetail ? Math.max(0, otherCols.length - MAX_EXTRA_COLS) : 0;
  const height = columnDetail ?
    BACK_HEADER + displayCols.length * ROW_H + (moreCount ? MORE_H : 0) + 10 :
    FRONT_H;
  return {
    objectId: n.objectId || n.id,
    objectType: (n.objectType || 'table').toLowerCase(),
    label: n.objectName || n.label,
    badges: n.badges || [],
    colTotal: cols.length || null,
    displayCols,
    moreCount,
    height,
  };
}

function layoutPositions(specs, edges) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({rankdir: 'LR', ranksep: 150, nodesep: 72});
  specs.forEach((s) => g.setNode(s.objectId, {width: NODE_WIDTH, height: s.height}));
  edges.forEach((e) => {
    if (g.hasNode(e.src) && g.hasNode(e.tgt)) g.setEdge(e.src, e.tgt);
  });
  dagre.layout(g);
  const pos = {};
  specs.forEach((s) => {
    const p = g.node(s.objectId);
    pos[s.objectId] = {x: p.x - NODE_WIDTH / 2, y: p.y - s.height / 2};
  });
  return pos;
}

// BFS upstream (things the object depends on) and downstream (things depending on it).
function traverse(edges, startId, depth) {
  const downAdj = new Map();
  const upAdj = new Map();
  edges.forEach((e) => {
    if (!downAdj.has(e.src)) downAdj.set(e.src, []);
    downAdj.get(e.src).push({node: e.tgt, edgeId: e.id});
    if (!upAdj.has(e.tgt)) upAdj.set(e.tgt, []);
    upAdj.get(e.tgt).push({node: e.src, edgeId: e.id});
  });
  const bfs = (adj) => {
    const nodes = new Set();
    const edgeIds = new Set();
    const seen = new Set([startId]);
    let frontier = [startId];
    let level = 0;
    while (frontier.length && (depth == null || level < depth)) {
      const next = [];
      frontier.forEach((n) => {
        (adj.get(n) || []).forEach(({node, edgeId}) => {
          edgeIds.add(edgeId);
          if (!seen.has(node)) {
            seen.add(node);
            nodes.add(node);
            next.push(node);
          }
        });
      });
      frontier = next;
      level += 1;
    }
    return {nodes, edgeIds};
  };
  const down = bfs(downAdj);
  const up = bfs(upAdj);
  return {
    upstreamEdges: up.edgeIds,
    downstreamEdges: down.edgeIds,
    nodes: new Set([startId, ...up.nodes, ...down.nodes]),
  };
}

// Column-level BFS over FK edges: upstream = columns this column references,
// downstream = columns that reference it. Returns the involved edges, column ids,
// and the object ids that own them (for node highlight/dim).
function traverseColumn(edges, startColId, depth) {
  const downAdj = new Map();
  const upAdj = new Map();
  edges.forEach((e) => {
    if (!e.parentColumnId || !e.childColumnId) return;
    if (!downAdj.has(e.parentColumnId)) downAdj.set(e.parentColumnId, []);
    downAdj.get(e.parentColumnId).push({col: e.childColumnId, edgeId: e.id, edge: e});
    if (!upAdj.has(e.childColumnId)) upAdj.set(e.childColumnId, []);
    upAdj.get(e.childColumnId).push({col: e.parentColumnId, edgeId: e.id, edge: e});
  });
  const bfs = (adj) => {
    const cols = new Set();
    const edgeIds = new Set();
    const nodes = new Set();
    const seen = new Set([startColId]);
    let frontier = [startColId];
    let level = 0;
    while (frontier.length && (depth == null || level < depth)) {
      const next = [];
      frontier.forEach((cid) => {
        (adj.get(cid) || []).forEach(({col, edgeId, edge}) => {
          edgeIds.add(edgeId);
          cols.add(col);
          cols.add(cid);
          nodes.add(edge.src);
          nodes.add(edge.tgt);
          if (!seen.has(col)) {
            seen.add(col);
            next.push(col);
          }
        });
      });
      frontier = next;
      level += 1;
    }
    return {cols, edgeIds, nodes};
  };
  const down = bfs(downAdj);
  const up = bfs(upAdj);
  return {
    upstreamEdges: up.edgeIds,
    downstreamEdges: down.edgeIds,
    columnIds: new Set([startColId, ...up.cols, ...down.cols]),
    nodeIds: new Set([...up.nodes, ...down.nodes]),
  };
}

class RiDiagram extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedNodeId: null,
      selectedColumn: null,
      hoveredNodeId: null,
      highlightMode: 'full',
      positionOverrides: {},
    };
    this._sig = null;
    this._positions = {};
    this.handleNodeClick = this.handleNodeClick.bind(this);
    this.handlePaneClick = this.handlePaneClick.bind(this);
    this.handleNodeEnter = this.handleNodeEnter.bind(this);
    this.handleNodeLeave = this.handleNodeLeave.bind(this);
    this.handleNodesChange = this.handleNodesChange.bind(this);
    this.handleColumnClick = this.handleColumnClick.bind(this);
  }

  // Click a column inside a node → highlight only that column's FK relationships.
  handleColumnClick(objectId, columnId) {
    this.setState((prev) => {
      const same = prev.selectedColumn && prev.selectedColumn.columnId === columnId;
      return {
        selectedColumn: same ? null : {objectId, columnId},
        selectedNodeId: null,
        hoveredNodeId: null,
      };
    });
  }

  // Capture drag moves as position overrides so dragged nodes stay put instead of
  // snapping back to the dagre layout on the next render.
  handleNodesChange(changes) {
    const moves = changes.filter((c) => c.type === 'position' && c.position);
    if (moves.length === 0) return;
    this.setState((prev) => {
      const overrides = {...prev.positionOverrides};
      moves.forEach((c) => {
        overrides[c.id] = c.position;
      });
      return {positionOverrides: overrides};
    });
  }

  componentDidUpdate(prevProps) {
    // A new schema or a re-layout (column-detail flip) invalidates manual positions.
    if (prevProps.graph !== this.props.graph) {
      this.setState({selectedNodeId: null, selectedColumn: null, hoveredNodeId: null, positionOverrides: {}});
    }
    if (prevProps.columnDetail !== this.props.columnDetail) {
      // leaving column-detail hides columns, so a column selection is no longer valid
      this.setState({positionOverrides: {}, selectedColumn: null});
      if (this.rf) setTimeout(() => this.rf && this.rf.fitView({padding: 0.2}), 80);
    }
  }

  handleNodeClick(e, node) {
    this.setState((prev) => ({
      selectedNodeId: prev.selectedNodeId === node.id ? null : node.id,
      selectedColumn: null,
      hoveredNodeId: null,
    }));
  }

  handlePaneClick() {
    if (this.state.selectedNodeId || this.state.selectedColumn) {
      this.setState({selectedNodeId: null, selectedColumn: null});
    }
  }

  handleNodeEnter(e, node) {
    if (!this.state.selectedNodeId) this.setState({hoveredNodeId: node.id});
  }

  handleNodeLeave() {
    if (this.state.hoveredNodeId) this.setState({hoveredNodeId: null});
  }

  buildFlowElements() {
    const {graph, columnsByObject, columnDetail, onExpandMore, filters, search} = this.props;
    if (!graph) return {nodes: [], edges: []};

    const {hoveredNodeId, highlightMode, positionOverrides} = this.state;
    const {nodes: graphNodes, edges: graphEdges} = filterGraph(graph, filters, search);

    // A selection filtered out of view is treated as no selection for this render
    // — otherwise a hidden node dims the whole canvas and shows a phantom inspector.
    const visibleIds = new Set(graphNodes.map((n) => n.objectId || n.id));
    let {selectedNodeId, selectedColumn} = this.state;
    if (selectedNodeId && !visibleIds.has(selectedNodeId)) selectedNodeId = null;
    if (selectedColumn && !visibleIds.has(selectedColumn.objectId)) selectedColumn = null;

    const nEdges = graphEdges.map((e, i) => ({
      id: `edge-${i}`,
      src: e.parentObjectId || e.source,
      tgt: e.childObjectId || e.target,
      relType: e.relType || e.type || 'FK',
      constraintName: e.constraintName || '',
      parentColumnId: e.parentColumnId,
      childColumnId: e.childColumnId,
    }));

    const connectedColIds = new Set();
    nEdges.forEach((e) => {
      if (e.parentColumnId) connectedColIds.add(e.parentColumnId);
      if (e.childColumnId) connectedColIds.add(e.childColumnId);
    });

    const specs = graphNodes.map((n) => buildNodeSpec(n, columnsByObject, columnDetail, connectedColIds));

    // Layout is cached — only recomputed when the node set / column-detail changes,
    // so hover and selection restyle without re-running dagre (no jitter).
    const f = filters || {};
    const sig = `${columnDetail}|${specs.map((s) => s.objectId).join(',')}|${nEdges.length}` +
      `|${search || ''}|${f.type || ''}|${f.relType || ''}|${f.crossSchema ? 1 : 0}`;
    if (sig !== this._sig) {
      this._positions = layoutPositions(specs, nEdges);
      this._sig = sig;
    }

    const activeId = selectedNodeId || hoveredNodeId;
    const nodeDepth = selectedNodeId ? (highlightMode === 'immediate' ? 1 : null) : 1;
    const trav = (!selectedColumn && activeId) ? traverse(nEdges, activeId, nodeDepth) : null;
    const colDepth = highlightMode === 'immediate' ? 1 : null;
    const colTrav = selectedColumn ? traverseColumn(nEdges, selectedColumn.columnId, colDepth) : null;

    const nodes = specs.map((s) => {
      let highlighted = false;
      let dimmed = false;
      if (colTrav) {
        const nodeHl = colTrav.nodeIds.has(s.objectId) || s.objectId === selectedColumn.objectId;
        highlighted = nodeHl;
        dimmed = !nodeHl;
      } else if (trav) {
        const inPath = trav.nodes.has(s.objectId);
        highlighted = inPath && activeId !== s.objectId;
        dimmed = !!(selectedNodeId && !inPath);
      }
      return {
        id: s.objectId,
        type: 'riColumn',
        position: positionOverrides[s.objectId] || this._positions[s.objectId] || {x: 0, y: 0},
        selected: selectedNodeId === s.objectId,
        data: {
          label: s.label,
          objectType: s.objectType,
          columns: s.displayCols,
          colTotal: s.colTotal,
          expanded: columnDetail,
          moreCount: s.moreCount,
          badges: s.badges,
          onExpandMore,
          onColumnClick: this.handleColumnClick,
          columnSelectionActive: !!colTrav,
          highlightedColumns: colTrav ? colTrav.columnIds : null,
          selectedColumnId: selectedColumn ? selectedColumn.columnId : null,
          highlighted,
          dimmed,
        },
      };
    });

    const useColHandles = columnDetail;
    const edges = nEdges.map((e) => {
      const base = baseEdge(e.relType);
      let stroke = base.color;
      let animated = true;
      let width = 1.6;
      let opacity = 0.9;
      let onPath = false;

      if (colTrav) {
        const isUp = colTrav.upstreamEdges.has(e.id);
        const isDown = colTrav.downstreamEdges.has(e.id);
        onPath = isUp || isDown;
        if (onPath) {
          stroke = isUp ? UP_COLOR : DOWN_COLOR;
          width = 2.6;
          opacity = 1;
        } else {
          // column selected — hide everything not touching that column
          stroke = DIM_EDGE;
          animated = false;
          opacity = 0.1;
        }
      } else if (trav) {
        const isUp = trav.upstreamEdges.has(e.id);
        const isDown = trav.downstreamEdges.has(e.id);
        onPath = isUp || isDown;
        if (onPath) {
          stroke = isUp ? UP_COLOR : DOWN_COLOR;
          width = 2.6;
          opacity = 1;
        } else if (selectedNodeId) {
          stroke = DIM_EDGE;
          animated = false;
          opacity = 0.2;
        }
      }

      const colEdge = useColHandles && e.relType === 'FK' && e.parentColumnId && e.childColumnId;
      return {
        id: e.id,
        source: e.src,
        target: e.tgt,
        sourceHandle: colEdge ? `col-${e.parentColumnId}-source` : 'node-source',
        targetHandle: colEdge ? `col-${e.childColumnId}-target` : 'node-target',
        // Curved (bezier) edges route around node bodies far better than orthogonal
        // smoothstep, which was crossing through opaque nodes.
        type: 'default',
        animated,
        markerEnd: {type: MarkerType.ArrowClosed, width: 14, height: 14, color: stroke},
        style: {stroke, strokeWidth: width, strokeDasharray: base.dash || undefined, opacity},
        // Show the constraint label only for edges on the selected/hovered path — keeps
        // the idle graph uncluttered (all labels are in the Table view anyway).
        label: onPath ? e.constraintName : '',
        labelStyle: {fontSize: 11, fill: '#333', fontWeight: 600},
        labelBgStyle: {fill: '#ffffff', fillOpacity: 0.9},
        labelBgPadding: [4, 2],
        labelBgBorderRadius: 3,
      };
    });

    return {nodes, edges};
  }

  renderInspector(visibleNodes) {
    const {graph, onOpenImpact} = this.props;
    const {selectedNodeId} = this.state;
    if (!selectedNodeId || !graph) return null;
    if (!visibleNodes.some((n) => n.id === selectedNodeId)) return null;

    const node = (graph.nodes || []).find((n) => (n.objectId || n.id) === selectedNodeId);
    if (!node) return null;

    const edges = graph.edges || [];
    const upCount = edges.filter((e) => (e.childObjectId || e.target) === selectedNodeId).length;
    const downCount = edges.filter((e) => (e.parentObjectId || e.source) === selectedNodeId).length;

    return (
      <div className="ri-node-inspector">
        <strong>{node.objectName || node.label}</strong>
        <div className="ri-inspector-stat">
          <strong style={{color: UP_COLOR}}>{upCount}</strong>
          <span>upstream (depends on)</span>
        </div>
        <div className="ri-inspector-stat">
          <strong style={{color: DOWN_COLOR}}>{downCount}</strong>
          <span>downstream (depended on)</span>
        </div>
        {onOpenImpact && (
          <button
            type="button"
            className="btn btn-default"
            data-testid="ri-inspector-impact"
            onClick={() => onOpenImpact(selectedNodeId)}
          >
            Analyze impact
          </button>
        )}
      </div>
    );
  }

  render() {
    const {nodes, edges} = this.buildFlowElements();
    const {selectedNodeId, selectedColumn, highlightMode} = this.state;

    const legend = [
      {color: FK_COLOR, label: 'FK (1:N)'},
      {color: VIEW_COLOR, dashed: true, label: 'View dependency'},
      {color: CIRCULAR_COLOR, dashed: true, label: 'Circular'},
      {color: UP_COLOR, label: 'Upstream (on select)'},
      {color: DOWN_COLOR, label: 'Downstream (on select)'},
    ];

    return (
      <div>
        <div className="ri-diagram-wrap">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            nodesDraggable
            nodesConnectable={false}
            elementsSelectable
            minZoom={0.2}
            maxZoom={2}
            fitView
            onInit={(inst) => {
              this.rf = inst;
            }}
            onNodesChange={this.handleNodesChange}
            onNodeClick={this.handleNodeClick}
            onPaneClick={this.handlePaneClick}
            onNodeMouseEnter={this.handleNodeEnter}
            onNodeMouseLeave={this.handleNodeLeave}
          >
            <Controls />
            <MiniMap
              pannable
              zoomable
              nodeColor={(n) => (n.data && n.data.objectType === 'view' ? VIEW_COLOR : FK_COLOR)}
              nodeStrokeWidth={2}
            />
            <Background />
          </ReactFlow>

          {/* Highlight-depth toggle (applies to the selected node's path) */}
          <div className="ri-diagram-toolbar">
            <span className="ri-diagram-toolbar-label">Highlight</span>
            <button
              type="button"
              className={`btn ${highlightMode === 'immediate' ? 'btn-action' : 'btn-default'}`}
              data-testid="ri-highlight-immediate"
              onClick={() => this.setState({highlightMode: 'immediate'})}
            >
              Immediate
            </button>
            <button
              type="button"
              className={`btn ${highlightMode === 'full' ? 'btn-action' : 'btn-default'}`}
              data-testid="ri-highlight-full"
              onClick={() => this.setState({highlightMode: 'full'})}
            >
              Full path
            </button>
          </div>

          <div className="ri-legend">
            {legend.map((item) => (
              <div key={item.label} className="ri-legend-row">
                <span
                  className="ri-legend-dot"
                  style={{
                    background: item.dashed ? 'transparent' : item.color,
                    border: item.dashed ? `2px dashed ${item.color}` : `2px solid ${item.color}`,
                  }}
                />
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          {!selectedNodeId && !selectedColumn && (nodes.length > 0) && (
            <div className="ri-diagram-hint">
              {this.props.columnDetail ?
                'Click a node — or a column — to highlight its relationships' :
                'Click a node to highlight its dependency path'}
            </div>
          )}
        </div>

        {this.renderInspector(nodes)}
      </div>
    );
  }
}

export default RiDiagram;
